import React, { useCallback, useContext, useEffect, useState } from 'react';
import RecapFormalityShow from '@europrocurement/l2d-modules/modules/ParcoursFormalite/components/RecapFormalityShow';
import { Box, Container } from '@mui/system';
import {
    CollectionDeDossierApi,
    CollectionDeDossierCollectiondossierRead,
    CollectionDeDossierJsonldCollectiondossierRead,
    FproDossierCollectiondossierRead,
    MessageApi,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    DossierFormaliteApi,
    MESSAGE_FROM_PRESCRIBER,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { useModal } from '@europrocurement/flexy-components';
import { useParcoursFormalite, useParcoursFormalitreHeader } from '../provider';
import { parcoursFormalitePathResolver } from '../parcoursFormalitePathResolver';
import { DossierPrescriberContext } from '../../Dossiers/providers';

const updateRefInterne = async function (
    refInterne: string | null,
    innerDossier: FproDossierCollectiondossierRead,
    dossierApi: DossierFormaliteApi,
) {
    const freshDossier = (await dossierApi.apiDossiersIdGet({ id: `${innerDossier.id}` })).data;

    if (refInterne !== freshDossier.societeRef) {
        await dossierApi.apiDossiersIdPut({
            id: `${innerDossier.id}`,
            dossierJsonldDossierUpdate: {
                societeDenomination: freshDossier.societeDenomination,
                jsonObject: freshDossier.jsonObject,
                societeSiren: freshDossier.societeSiren,
                acompteAttendu: freshDossier.acompteAttendu,
                affaire: freshDossier.affaire,
                collection: freshDossier.collection,
                societeRef: refInterne,
            },
        });
    }
};

const updateCollectionUrgent = async function (
    currentColelction: CollectionDeDossierJsonldCollectiondossierRead,
    dossierUrgent: boolean,
    clientId: number,
    collectionApi: CollectionDeDossierApi,
    callback: (collection: CollectionDeDossierJsonldCollectiondossierRead) => void,
    indirectSale: boolean,
) {
    const freshCollection = (
        await collectionApi.apiCollectionsDossiersIdGet({ id: `${currentColelction.id}` })
    ).data;

    if (!freshCollection || !freshCollection.id) {
        throw new Error('missing freshCollection');
    }

    if (!freshCollection.client) {
        throw new Error('missing client');
    }
    if (!freshCollection.prescripteur) {
        throw new Error('missing prescripteur');
    }

    if (
        dossierUrgent !== freshCollection.urgent ||
        clientId !== Number(freshCollection.client) ||
        indirectSale !== freshCollection.venteIndirecte
    ) {
        await collectionApi
            .apiCollectionsDossiersIdPatch({
                id: `${freshCollection?.id}`,
                collectionDeDossierCollectiondossierUpdate: {
                    prescripteur: freshCollection.prescripteur?.replace(
                        '/tiers/tiers',
                        '/formalite/tiers',
                    ),
                    urgent: dossierUrgent === true,
                    client: `/formalite/tiers/${clientId}`,
                    infoComp: freshCollection.infoComp,
                    libelle: freshCollection.libelle,
                    venteIndirecte: indirectSale,
                },
            })
            .then((response) => {
                callback(response.data);
            });
    }
};

const updateMessageDossier = async function (
    commentaires: string | null,
    innerDossier: FproDossierCollectiondossierRead,
    innerMessageApi: MessageApi,
    kc: ReturnType<typeof UseKeycloakService>,
) {
    if (commentaires && commentaires !== '' && innerDossier.id) {
        await innerMessageApi
            .apiDossiersDossierMessagesGetCollection({
                dossier: innerDossier.id as unknown as string,
            })
            .then((response) => {
                response.data['hydra:member'].forEach((message) => {
                    if (message.id !== undefined) {
                        innerMessageApi.apiMessagesIdDelete({
                            id: message.id as unknown as string,
                        });
                    }
                });
            });

        await innerMessageApi.apiMessagesPost({
            messageJsonldMessageCreate: {
                expediteur: MESSAGE_FROM_PRESCRIBER,
                expediteurNom: kc.getName(),
                message: commentaires,
                dossier: `/formalite/dossiers/${innerDossier.id}`,
            },
        });
    }
};

const StepRecap: React.FunctionComponent = function () {
    const snackbar = useSnackbar();

    const { getCurrentCollection, getPrescripteur, currentSousClient, setLoading } =
        useParcoursFormalite();

    React.useEffect(() => {
        setLoading(false);
    }, [setLoading]);

    const { dossierApi, collectionApi, messageApi } = useFormaliteService();
    const { sousClients, prescriberLoaded, setPrescriberId } = useContext(DossierPrescriberContext);
    const kc = UseKeycloakService();

    const [collection, setCollection] =
        useState<CollectionDeDossierJsonldCollectiondossierRead | null>(getCurrentCollection());

    const navigate = useNavigate();
    const prescripteur = getPrescripteur();
    const sousClient = currentSousClient;

    useEffect(() => {
        if (prescripteur && prescripteur.id) {
            setPrescriberId(prescripteur.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prescripteur]);

    const { changeParcoursSteps, setIsParcoursPath } = useParcoursFormalitreHeader();
    const { validate } = useParcoursFormalite();
    const { setProfileHeaderVisible } = useFullLayout();

    useEffect(() => {
        changeParcoursSteps(4);
        setProfileHeaderVisible(false);
        setIsParcoursPath(true);
        return () => {
            setIsParcoursPath(false);
            setProfileHeaderVisible(true);
        };
    }, [setProfileHeaderVisible, changeParcoursSteps, setIsParcoursPath]);

    const modals = useModal();

    const onChangeHandler = useCallback(
        async (
            innerCollection: CollectionDeDossierCollectiondossierRead,
            refInterne: string | null,
            commentaires: string | null,
            dossierUrgent: boolean | null,
            indirectSale: boolean,
            clientId: number,
        ) => {
            if (innerCollection.dossiers?.length !== 1) {
                throw new Error('error in dossier length');
            }
            if (!collection || !collection.id) {
                throw new Error('error in Collection');
            }
            const innerDossier = innerCollection.dossiers[0];

            Promise.all([
                updateRefInterne(refInterne, innerDossier, dossierApi),
                updateCollectionUrgent(
                    collection,
                    dossierUrgent === true,
                    clientId,
                    collectionApi,
                    (updatedCollection) => {
                        setCollection(updatedCollection);
                    },
                    indirectSale,
                ),
                updateMessageDossier(commentaires, innerDossier, messageApi, kc),
            ]);
        },
        [dossierApi, collection, collectionApi, messageApi, kc],
    );

    const onSubmitHandler = useCallback(
        async (
            innerCollection: CollectionDeDossierCollectiondossierRead,
            commentaires: string | null,
        ) => {
            setLoading(true);
            if (innerCollection.dossiers?.length !== 1) {
                throw new Error('error in dossier length');
            }

            const res = await validate(collection);

            if (commentaires && commentaires.trim() !== '' && res.data.dossiers) {
                try {
                    await Promise.all(
                        res.data.dossiers.map((dossier) =>
                            dossierApi.apiDossiersIdNotesPost({
                                id: String(dossier.id),
                                apiDossiersIdNotesPostRequest: {
                                    content: commentaires,
                                    highlighted: false,
                                    memoClient: false,
                                },
                            }),
                        ),
                    );
                } catch {
                    // note creation failure is non-blocking
                }
            }

            setTimeout(() => {
                setLoading(false);

                snackbar.enqueueSnackbar('Dossier envoyé !');
                if (res.data.dossiers) {
                    if (res.data.dossiers.length > 1) {
                        modals.modalActions.call(
                            <Box
                                sx={{
                                    maxWidth: 'sm',
                                    flexDirection: 'column',
                                    gap: 2,
                                    display: 'flex',
                                }}
                            >
                                <Typography
                                    variant="l2dh6"
                                    fontWeight={600}
                                >
                                    Plusieurs dossiers ont été créés pour traiter correctement votre
                                    commande.
                                </Typography>
                                <Typography>
                                    Vous pouvez les retrouver dans la liste des dossiers ou naviguer
                                    dans les différents dossiers de la commande en bas de la fiche
                                    dossier.
                                </Typography>
                                <Button
                                    onClick={() => {
                                        navigate(
                                            `/dossiers${res.data.dossiers ? `/${res.data.dossiers[0].id}` : ''}`,
                                        );
                                        modals.modalActions.reset();
                                    }}
                                >
                                    Voir le premier dossier
                                </Button>
                            </Box>,
                            false,
                            false,
                            () => {
                                navigate(
                                    `/dossiers${res.data.dossiers ? `/${res.data.dossiers[0].id}` : ''}`,
                                );
                            },
                        );
                    } else {
                        navigate(`/dossiers/${res.data.dossiers[0].id}`);
                    }
                }
            }, 1500);
        },
        [setLoading, collection, dossierApi, snackbar, navigate, modals.modalActions, validate],
    );

    if (!collection) {
        return null;
    }
    return (
        <Container>
            {prescriberLoaded && prescripteur ? (
                <RecapFormalityShow
                    onPrevious={() => {
                        navigate(parcoursFormalitePathResolver('stepDocs'));
                    }}
                    sousClients={sousClients}
                    collection={collection}
                    prescripteur={prescripteur}
                    sousClient={sousClient}
                    onChange={onChangeHandler}
                    onSubmit={onSubmitHandler}
                    commentairesDossier=""
                />
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default StepRecap;
